import React from 'react';
import { User, UserCog, AlertTriangle } from 'lucide-react';
import UserSearch from './UserSearch';
import { useAuth } from '../../contexts/AuthContext';

interface UserInfo {
  user_id: string;
  email: string;
  email_verified: boolean;
  name?: string;
  phone_number?: string;
  created_at?: string;
  updated_at?: string;
}

const ImpersonationSection: React.FC = () => {
  const { user, startImpersonation, impersonationContext, stopImpersonation } = useAuth();

  const handleUserSelect = (userInfo: UserInfo) => {
    // Create user object compatible with auth context
    const targetUser = {
      id: userInfo.user_id,
      email: userInfo.email,
      name: userInfo.name || userInfo.email.split('@')[0],
      username: userInfo.email.split('@')[0],
      groups: [], // Impersonated users don't inherit admin groups
    };

    startImpersonation(userInfo.user_id, targetUser);
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-6">
        User Impersonation
      </h2>

      {/* Current Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
          <div className="flex items-center">
            <UserCog size={20} className={`mr-3 ${impersonationContext.isImpersonating ? 'text-orange-500' : 'text-green-500'}`} />
            <div>
              <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
                Impersonation Status
              </h3>
              <p className={`text-sm ${impersonationContext.isImpersonating ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                {impersonationContext.isImpersonating ? 'Active' : 'Inactive'}
              </p>
              {impersonationContext.isImpersonating && impersonationContext.targetUser && (
                <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                  Impersonating: {impersonationContext.targetUser.email}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-900 p-4 rounded-lg">
          <div className="flex items-center">
            <User size={20} className="text-blue-500 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
                Admin User
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {impersonationContext.originalAdminUser?.email || user?.email || 'Unknown'}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                Admin permissions active
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Impersonation Controls */}
      {!impersonationContext.isImpersonating ? (
        <div>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            Search for and select a user to impersonate. This allows you to view the platform as that user.
          </p>
          <UserSearch
            onUserSelect={handleUserSelect}
            disabled={false}
            label="User ID or Email to Impersonate"
            placeholder="Enter user ID or email (e.g., user@example.com)"
            buttonText="Start Impersonation"
            helpText={
              <>
                <p>Enter either a <strong>user ID</strong> or <strong>email address</strong> to validate and impersonate.</p>
                <p>• Email search is case-insensitive (e.g., USER@EXAMPLE.COM will find user@example.com)</p>
                <p>• User must exist in the system database</p>
              </>
            }
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <AlertTriangle size={20} className="text-orange-600 dark:text-orange-400 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-orange-800 dark:text-orange-200 font-medium">
                Currently impersonating: {impersonationContext.targetUser?.email}
              </p>
              <p className="text-orange-700 dark:text-orange-300 text-sm">
                Impersonation will auto-stop after 30 minutes for security.
              </p>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={stopImpersonation}
              className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
            >
              Stop Impersonation
            </button>
          </div>
        </div>
      )}

      {/* System Information */}
      <div className="mt-8 pt-8 border-t border-neutral-200 dark:border-neutral-700">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
          System Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-neutral-700 dark:text-neutral-300">Admin User:</span>
            <p className="text-neutral-900 dark:text-white">{user?.email || 'Unknown'}</p>
          </div>
          <div>
            <span className="font-medium text-neutral-700 dark:text-neutral-300">Admin Groups:</span>
            <p className="text-neutral-900 dark:text-white">
              {user?.groups?.join(', ') || 'None'}
            </p>
          </div>
          <div>
            <span className="font-medium text-neutral-700 dark:text-neutral-300">Impersonation Active:</span>
            <p className="text-neutral-900 dark:text-white">
              {impersonationContext.isImpersonating ? 'Yes' : 'No'}
            </p>
          </div>
          <div>
            <span className="font-medium text-neutral-700 dark:text-neutral-300">Session Status:</span>
            <p className="text-green-600 dark:text-green-400">Active</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationSection; 